import type { DrillFilter } from "../../types/status";

export function toDatetimeLocal(isoUtc: string): string {
  const d = new Date(isoUtc.endsWith("Z") ? isoUtc : isoUtc + "Z");
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

export function timeAgo(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const utc = dateStr.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + "Z";
  const diff = Math.floor((Date.now() - new Date(utc).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return "—";
  const utc = dateStr.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + "Z";
  return new Date(utc).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function incidentDuration(startedAt: string, resolvedAt?: string | null): string {
  if (!resolvedAt) return "ongoing";
  const norm = (s: string) => (s.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(s) ? s : s + "Z");
  const secs = Math.max(0, Math.round((new Date(norm(resolvedAt)).getTime() - new Date(norm(startedAt)).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const CHANGELOG_FIELD_LABEL: Record<string, string> = {
  severity: "Severity",
  status: "Status",
  resolved_at: "Resolved at",
  root_cause: "Root cause",
  notes: "Notes",
  registrations_lost: "Registrations lost",
  ggr_impact: "GGR impact",
};

export function changelogChanges(meta: Record<string, unknown> | null | undefined): string[] {
  const changes = (meta as { changes?: Record<string, { old: unknown; new: unknown }> } | null)?.changes;
  if (!changes) return [];
  const show = (x: unknown) => (x === null || x === undefined || x === "" ? "—" : String(x));
  return Object.entries(changes).map(([field, v]) => `${CHANGELOG_FIELD_LABEL[field] ?? field}: ${show(v.old)} → ${show(v.new)}`);
}

const DRILL_KEYS = ["dfIds", "dfFrom", "dfTo", "dfSeverity", "dfType", "dfStatus", "dfLabel"];

export function clearDrillParams(params: URLSearchParams): void {
  DRILL_KEYS.forEach((key) => params.delete(key));
}

export function writeDrillFilter(params: URLSearchParams, filter: DrillFilter): void {
  clearDrillParams(params);
  if (filter.ids?.length) params.set("dfIds", filter.ids.join(","));
  if (filter.from) params.set("dfFrom", filter.from);
  if (filter.to) params.set("dfTo", filter.to);
  if (filter.severity) params.set("dfSeverity", filter.severity);
  if (filter.serviceType) params.set("dfType", filter.serviceType);
  if (filter.status) params.set("dfStatus", filter.status);
  params.set("dfLabel", filter.label);
}

export function parseDrillFilter(params: URLSearchParams): DrillFilter | null {
  const label = params.get("dfLabel");
  const ids = params.get("dfIds");
  const from = params.get("dfFrom");
  const to = params.get("dfTo");
  const severity = params.get("dfSeverity");
  const serviceType = params.get("dfType");
  const status = params.get("dfStatus");
  if (!label && !ids && !from && !to && !severity && !serviceType && !status) return null;
  return {
    label: label ?? "Filtered incidents",
    ids: ids ? ids.split(",") : undefined,
    from: from ?? undefined,
    to: to ?? undefined,
    severity: severity ?? undefined,
    serviceType: serviceType ?? undefined,
    status: status ?? undefined,
  };
}
